/**
 * RWBR — BRING YOUR OWN MODEL. D-EZ-48, the browser half.
 *
 * Howard, 18 Aug 2026:
 *
 *   "we need to add the same Login option where a user OTHER than me puts their
 *    own api model in and key and the ai uses their api key for the commentary…
 *    but lock in the user's api when they login, or if they dont add one then
 *    the commentary is simply fallback cached commentary with no api calls."
 *
 * ===========================================================================
 * THREE STATES, AND THE THIRD ONE IS NOT A FAILURE
 * ===========================================================================
 *
 *   OWNER   — Howard, SIGNED IN, on the deployed Worker. No key here: his
 *             session token rides on the request, the Worker verifies it is
 *             his, and spends `OPENAI_API_KEY`. D-EZ-100: *"any user brings
 *             their own. Mine is for me."* — the owner key is his alone.
 *   PLAYER  — has locked in a key. It rides on every AI request as a header,
 *             the Worker spends it for that one call, and it is never stored
 *             anywhere but this browser.
 *   NOBODY  — no key at either end. **No API call is made at all.** The booth
 *             falls back to its deterministic lines, which it has always been
 *             built to do, and the newspaper is written by
 *             `domain/paper/fallbackPaper.ts` out of the same facts. A player in
 *             this state gets a complete game, not a broken one.
 *
 * ===========================================================================
 * WHERE THE KEY LIVES, AND THE PART THAT IS NOT COMFORTABLE
 * ===========================================================================
 *
 * In this browser. `localStorage` if the player asked to be remembered,
 * `sessionStorage` if they did not, and nowhere else: it is never written to
 * Supabase, never saved into a league, never put in a URL, and never included in
 * anything the app exports or prints.
 *
 * ⚠ IT DOES TRAVEL. Every AI request goes browser → this app's Cloudflare Worker
 * → OpenAI, and the Worker holds the key in memory for the length of one
 * request. That is TLS on both legs and nothing is persisted, but it is not the
 * same as never leaving the machine, and the panel says so in those words. The
 * reason the proxy exists at all is that the prompts — the booth's character
 * briefs, the paper's masthead and rules — must not ship in the bundle, or a
 * player could rewrite them from the console.
 *
 * ⚠ AND `localStorage` IS READABLE BY ANY SCRIPT ON THIS ORIGIN. That is the
 * honest cost of "remember me". A player on a shared machine should leave the
 * box unticked, which keeps the key in `sessionStorage` and loses it when the
 * tab closes.
 *
 * A player should use a key scoped and budgeted for this, and should be able to
 * revoke it at OpenAI without breaking anything else. The panel says that too.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase.ts';
import { isOwner } from './auth.ts';

/** Bumped if the stored shape ever changes in a way a reader would notice. */
const STORAGE_KEY = 'rwbr.ai.byo.v1';

export interface AiSetup {
  /** The key itself. Never leaves this module except into a request header. */
  key: string;
  model: string;
  /** ISO. What the panel shows as "locked in". */
  savedAt: string;
  /** True when it is in `localStorage` rather than `sessionStorage`. */
  remembered: boolean;
}

/**
 * The models worth offering, newest first.
 *
 * A LIST, NOT A LOCK. The field is free text underneath, because a player may
 * have a fine-tuned model or access to something this list has never heard of,
 * and an app that refuses a valid model id because it is not in a dropdown is
 * an app arguing with its user about their own account.
 */
export const SUGGESTED_MODELS: readonly { id: string; label: string; note: string }[] = [
  {
    id: 'gpt-6-luna',
    label: 'GPT-6 Luna',
    note: 'What this game is tuned for — fast, cheap, and it reasons. Start here.',
  },
  {
    id: 'gpt-6-sol',
    label: 'GPT-6 Sol',
    note: 'The bigger model. Richer prose and deeper reasoning, roughly 20x the price, slower between turns.',
  },
];

export const DEFAULT_MODEL = SUGGESTED_MODELS[0].id;

export const KEY_HEADER = 'x-rwbr-ai-key';
export const MODEL_HEADER = 'x-rwbr-ai-model';

/**
 * ⚠ THE SAME SHAPE TEST THE WORKER RUNS, and deliberately duplicated.
 *
 * Not a security boundary — `worker/ai.ts` owns that, because the client's copy
 * of any check can be skipped by calling the API directly. This one exists to
 * tell a player who pasted the wrong thing what they pasted, at the moment they
 * paste it, instead of after a race with no commentary in it.
 */
export function looksLikeKey(raw: string): boolean {
  const key = raw.trim();
  return key.length >= 20 && key.length <= 256 && /^sk-[A-Za-z0-9_-]+$/.test(key);
}

/** What is wrong with this key, in a sentence, or null if nothing is. */
export function keyProblem(raw: string): string | null {
  const key = raw.trim();
  if (key.length === 0) return 'Paste a key, or leave this empty and take the written-in fallback.';
  if (key.startsWith('Bearer ')) return 'Drop the word Bearer — just the key itself.';
  if (!key.startsWith('sk-')) return 'An OpenAI key starts with sk-. This does not.';
  if (key.length < 20) return 'That is too short to be a whole key — check for a truncated paste.';
  if (key.length > 256) return 'That is far too long for a key. Check you have not pasted a whole file.';
  if (!/^sk-[A-Za-z0-9_-]+$/.test(key)) return 'There is a space or a stray character in there.';
  return null;
}

// ---------------------------------------------------------------------------
// Reading and writing
// ---------------------------------------------------------------------------

/**
 * A VERSION COUNTER, not an event emitter.
 *
 * `narrate.ts` caches "is narration available" for the whole sitting, which it
 * has to — without it the app would discover a missing key by waiting for a
 * timeout on every beat of every race. But that cache has to notice the moment
 * a player locks a key in, and a listener registry between two modules in
 * `src/lib` is a subscription to get wrong. A number that goes up is not.
 */
let version = 0;
export const aiSetupVersion = (): number => version;

function stores(): Storage[] {
  try {
    return [window.localStorage, window.sessionStorage];
  } catch {
    // Storage can throw outright in a locked-down browser. A game that will not
    // start because a key cannot be remembered is a worse game.
    return [];
  }
}

export function readSetup(): AiSetup | null {
  for (const [i, store] of stores().entries()) {
    let raw: string | null = null;
    try {
      raw = store.getItem(STORAGE_KEY);
    } catch {
      continue;
    }
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw) as Partial<AiSetup>;
      if (typeof parsed.key !== 'string' || !looksLikeKey(parsed.key)) continue;
      return {
        key: parsed.key,
        model: typeof parsed.model === 'string' && parsed.model ? parsed.model : DEFAULT_MODEL,
        savedAt: typeof parsed.savedAt === 'string' ? parsed.savedAt : '',
        remembered: i === 0,
      };
    } catch {
      continue;
    }
  }
  return null;
}

export interface SaveResult {
  ok: boolean;
  error?: string;
}

export function saveSetup(key: string, model: string, remember: boolean): SaveResult {
  const problem = keyProblem(key);
  if (problem) return { ok: false, error: problem };
  const clean = key.trim();
  const chosen = model.trim() || DEFAULT_MODEL;

  const setup: AiSetup = {
    key: clean,
    model: chosen,
    savedAt: new Date().toISOString(),
    remembered: remember,
  };

  try {
    // Written to ONE store and cleared from the other, so "remember me" being
    // turned off actually forgets rather than leaving a second copy behind that
    // `readSetup` would find first.
    window.localStorage.removeItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);
    const store = remember ? window.localStorage : window.sessionStorage;
    store.setItem(STORAGE_KEY, JSON.stringify(setup));
  } catch {
    return { ok: false, error: 'This browser will not let the page store anything. Check private-mode settings.' };
  }
  version += 1;
  return { ok: true };
}

export function clearSetup(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to forget */
  }
  version += 1;
}

/**
 * THE HEADERS FOR ONE AI REQUEST. `{}` when nobody has brought a key.
 *
 * Every caller spreads this into its own headers rather than reading the key,
 * so there is exactly one line in this application that puts a key into a
 * request and it is the one below.
 */
export function aiHeaders(): Record<string, string> {
  const setup = readSetup();
  if (!setup) return {};
  return { [KEY_HEADER]: setup.key, [MODEL_HEADER]: setup.model };
}

/** True when THIS browser is paying. False means the owner's key, or nobody's. */
export function hasOwnKey(): boolean {
  return readSetup() !== null;
}

// ---------------------------------------------------------------------------
// D-EZ-100 — may this browser call a model at all, and with what?
// ---------------------------------------------------------------------------

/** Who would pay for a model call from this browser right now. */
export type AiPayer = 'byo' | 'owner' | 'none';

export interface AiAccess {
  payer: AiPayer;
  /** Spread into the request. `{}` exactly when `payer` is 'none'. */
  headers: Record<string, string>;
}

/** The signed-in session, if any. Injectable so tests never touch Supabase. */
type SessionReader = () => Promise<{ email: string | null; token: string } | null>;

const readSupabaseSession: SessionReader = async () => {
  try {
    const { data } = await supabase.auth.getSession();
    const session = data?.session;
    if (!session?.access_token) return null;
    return { email: session.user?.email ?? null, token: session.access_token };
  } catch {
    return null;
  }
};

let readSession: SessionReader = readSupabaseSession;
let watchingAuth = false;

/** Test seam. Pass null to restore the real reader. */
export function setAiSessionReader(reader: SessionReader | null): void {
  readSession = reader ?? readSupabaseSession;
  version += 1;
}

/**
 * Signing in or out changes who pays, so it must invalidate the cached
 * "is narration available" answers exactly as locking in a key does. Hooked
 * lazily, on the first question, so importing this module costs nothing.
 */
function watchAuth(): void {
  if (watchingAuth || readSession !== readSupabaseSession) return;
  watchingAuth = true;
  try {
    supabase.auth.onAuthStateChange(() => {
      version += 1;
    });
  } catch {
    /* no auth in this environment; nothing to watch */
  }
}

/**
 * WHO PAYS, AND THE HEADERS THAT SAY SO — the one question every AI caller asks
 * before it calls anything.
 *
 *   byo   → the player's key and model, as before.
 *   owner → the owner's session token. The Worker verifies it with Supabase and
 *           only then spends the owner key; this side's `isOwner` is merely the
 *           reason to bother sending it.
 *   none  → `{}`, and the CALLER MUST NOT FETCH. A visitor with no key gets the
 *           written commentary, the game's own newspaper and the built-in names
 *           instantly — no request, no timeout, no red line in the console.
 */
export async function aiAccess(): Promise<AiAccess> {
  const setup = readSetup();
  if (setup) return { payer: 'byo', headers: { [KEY_HEADER]: setup.key, [MODEL_HEADER]: setup.model } };
  watchAuth();
  const session = await readSession();
  if (session && isOwner({ email: session.email ?? "" })) {
    return { payer: 'owner', headers: { Authorization: `Bearer ${session.token}` } };
  }
  return { payer: 'none', headers: {} };
}

/**
 * Read a Worker AI answer's "no model" signal. The Worker answers 200 with
 * `{ ok: false, noModel: true }` when nobody's key may be spent (D-EZ-100);
 * a caller treats that exactly like having no key.
 */
export function isNoModel(body: unknown): boolean {
  return Boolean(body && typeof body === 'object' && (body as { noModel?: unknown }).noModel === true);
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

export interface AiSetupHandle {
  setup: AiSetup | null;
  save: (key: string, model: string, remember: boolean) => SaveResult;
  forget: () => void;
}

export function useAiSetup(): AiSetupHandle {
  const [setup, setSetup] = useState<AiSetup | null>(() => readSetup());

  // A second tab that locks in a key should not leave this one thinking there
  // is none. `storage` only fires for OTHER tabs, which is exactly right.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === STORAGE_KEY) setSetup(readSetup());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const save = useCallback((key: string, model: string, remember: boolean) => {
    const result = saveSetup(key, model, remember);
    if (result.ok) setSetup(readSetup());
    return result;
  }, []);

  const forget = useCallback(() => {
    clearSetup();
    setSetup(null);
  }, []);

  return { setup, save, forget };
}

/**
 * A key, shown. `sk-proj-…dR4a` — enough to tell two keys apart and not enough
 * to be one. Nothing anywhere else in this app ever renders `setup.key`.
 */
export function maskKey(key: string): string {
  if (key.length <= 12) return 'sk-…';
  return `${key.slice(0, 7)}…${key.slice(-4)}`;
}
